import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, Clock, Loader2, FileText, User, Tag } from 'lucide-react';
import { useSearch } from '../hooks/useSearch';
import { useDebounce } from '../hooks/useDebounce';
import { getAuthorSlug } from '../utils/authorUtils';

interface SearchBarProps {
  variant?: 'navbar' | 'hero';
  onClose?: () => void;
  autoFocus?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ variant = 'navbar', onClose, autoFocus = false }) => {
  const navigate = useNavigate();
  const { query, setQuery, suggestions, getSuggestions } = useSearch();
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (autoFocus) {
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => window.clearTimeout(id);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      setLoading(true);
      getSuggestions(debouncedQuery).finally(() => setLoading(false));
    }
  }, [debouncedQuery, getSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsFocused(false);
      onClose?.();
    }
  };

  const handleSuggestionClick = (item: any) => {
    const { suggestion, source, metadata } = item;

    if (source === 'article' && metadata?.slug) {
      navigate(`/news/${metadata.slug}`);
    } else if (source === 'author' && suggestion) {
      if (metadata?.profile_url) {
        navigate(metadata.profile_url);
      } else {
        const authorSlug = getAuthorSlug(suggestion);
        navigate(`/writer/${authorSlug}`);
      }
    } else if (source === 'category' && suggestion) {
      navigate(`/category/${encodeURIComponent(suggestion)}`);
    } else {
      setQuery(suggestion);
      navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    }

    setIsFocused(false);
    onClose?.();
  };

  const isHero = variant === 'hero';

  return (
    <div ref={searchRef} className={`relative ${isHero ? 'w-full max-w-3xl' : 'w-full'}`}>
      <form onSubmit={handleSearch} className="relative">
        <div className={`relative flex items-center ${
          isHero
            ? 'bg-white rounded-full shadow-2xl'
            : 'bg-white rounded-lg border border-gray-200 focus-within:border-[#B6AC7D] focus-within:ring-2 focus-within:ring-[#B6AC7D]/20'
        } transition-all duration-200`}>
          <Search className={`absolute ${isHero ? 'left-6' : 'left-3'} ${
            isHero ? 'w-6 h-6' : 'w-5 h-5'
          } text-gray-400`} />

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            placeholder={isHero ? 'Search articles, authors, topics...' : 'Search...'}
            className={`w-full ${
              isHero
                ? 'pl-16 pr-32 py-5 text-lg'
                : 'pl-10 pr-24 py-2.5 text-sm'
            } bg-transparent border-0 focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400`}
          />

          {loading && (
            <div className={`absolute ${isHero ? 'right-32' : 'right-24'}`}>
              <Loader2 className={`${isHero ? 'w-5 h-5' : 'w-4 h-4'} text-[#B6AC7D] animate-spin`} />
            </div>
          )}

          <button
            type="submit"
            className={`absolute ${isHero ? 'right-2' : 'right-2'} ${
              isHero
                ? 'px-8 py-3 text-base'
                : 'px-4 py-1.5 text-sm'
            } bg-[#B6AC7D] hover:bg-[#A39B6D] text-white font-semibold rounded-full transition-colors`}
          >
            Search
          </button>
        </div>
      </form>

      {isFocused && (suggestions.length > 0 || query.length >= 2) && (
        <div className={`absolute z-50 w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden ${
          isHero ? 'max-h-96' : 'max-h-80'
        }`}>
          {suggestions.length > 0 ? (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Suggestions
              </div>
              {suggestions.map((item, index) => {
                const articleCount = item.metadata?.article_count ?? item.search_count ?? 0;
                const avatarUrl = item.metadata?.avatar_url;
                const imageUrl = item.metadata?.image_url;

                const renderLeading = () => {
                  if (item.source === 'article') {
                    if (imageUrl) {
                      return (
                        <img
                          src={imageUrl}
                          alt={item.suggestion}
                          loading="lazy"
                          className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0 bg-gray-100"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src =
                              'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=200';
                          }}
                        />
                      );
                    }
                    return (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-gray-500 group-hover:text-[#B6AC7D]" />
                      </div>
                    );
                  }

                  if (item.source === 'author' && avatarUrl) {
                    return (
                      <img
                        src={avatarUrl}
                        alt={item.suggestion}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      />
                    );
                  }

                  const iconWrap = 'w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0';
                  switch (item.source) {
                    case 'article':
                      return (
                        <div className={iconWrap}>
                          <FileText className="w-4 h-4 text-gray-500 group-hover:text-[#B6AC7D]" />
                        </div>
                      );
                    case 'author':
                      return (
                        <div className={iconWrap}>
                          <User className="w-4 h-4 text-gray-500 group-hover:text-[#B6AC7D]" />
                        </div>
                      );
                    case 'category':
                      return (
                        <div className={iconWrap}>
                          <Tag className="w-4 h-4 text-gray-500 group-hover:text-[#B6AC7D]" />
                        </div>
                      );
                    case 'history':
                      return (
                        <div className={iconWrap}>
                          <Clock className="w-4 h-4 text-gray-500 group-hover:text-[#B6AC7D]" />
                        </div>
                      );
                    default:
                      return (
                        <div className={iconWrap}>
                          <TrendingUp className="w-4 h-4 text-gray-500 group-hover:text-[#B6AC7D]" />
                        </div>
                      );
                  }
                };

                const getDescription = () => {
                  switch (item.source) {
                    case 'article':
                      return item.metadata?.category || 'Article';
                    case 'author':
                      return `Author • ${articleCount} ${articleCount === 1 ? 'article' : 'articles'}`;
                    case 'category':
                      return `Category • ${articleCount} ${articleCount === 1 ? 'article' : 'articles'}`;
                    case 'history':
                      return `${item.search_count} searches`;
                    default:
                      return item.search_count > 0 ? `${item.search_count} views` : '';
                  }
                };

                return (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(item)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors group"
                  >
                    {renderLeading()}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.suggestion}
                      </div>
                      {getDescription() && (
                        <div className="text-xs text-gray-500">
                          {getDescription()}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : query.length >= 2 && !loading && (
            <div className="px-4 py-8 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No suggestions found</p>
              <p className="text-xs mt-1">Press Enter to search</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
